// Story ▸ Cast & Places (StoryViews.swift's `EntitiesView`, `EntityEditor`): the characters, places, objects and motifs,
// each kind with its Add… button (characters also the Name Generator and "Study page for each character"), and the
// chosen one's details: name, kind, colour, other names (which the text is searched for), a character's role, study
// page and birth date, a place's position and clock (its time zone, or local mean time before time zones) and its
// distances to the other mapped places (TravelViews.tsx; Travel in the Story… under Places), notes, and Delete. Each
// field saves when it's committed (a second after typing stops, for notes), against the entity as stored
// now, so another device's edit to another field is never written back over.
import { useState } from 'react';
import { parseRGB, type Project } from '@writing-suite/core';
import { ENTITY_KIND_LABELS, suggestDoubling, type EntityNames } from '@writing-suite/core/story';
import { DialogShell } from '../plan/BeatBoard.tsx';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { announce } from '../../shell/context.ts';
import { followLink, navigate } from '../../shell/route.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import { characterStudiesOn, opened, storyEdit, storyModel, studyPagesOn, travelOf } from '../../store/story.ts';
import { lookUp } from '../../store/lookUp.ts';
import type { LiveProject } from '../../store/project.ts';
import { CommitInput, CommitTextArea } from '../FieldEditor.tsx';
import { TextDialog } from '../Margin.tsx';
import { ConfirmDialog } from '../Menu.tsx';
import { EntityDot, fromDateInput, plural, toDateInput, useShown } from './common.tsx';
import { NameGenerator } from './NameGenerator.tsx';
import { mappedPlacesBesides, PlaceDistances, TravelSheet } from './TravelViews.tsx';

type Kind = EntityNames['kind'];
type StoryEntity = ReturnType<typeof opened>['record']['entities'][number];
const KINDS: { kind: Kind; plural: string }[] = [
  { kind: 'character', plural: 'Characters' },
  { kind: 'place', plural: 'Places' },
  { kind: 'object', plural: 'Objects' },
  { kind: 'motif', plural: 'Motifs' },
];

export function CastPage(props: PageProps) {
  const { project, live, params, hrefFor } = props;
  const ws = useWorkspace();
  const canWrite = !!ws?.canWrite && !!live;
  const [adding, setAdding] = useState<Kind | null>(null);
  const [naming, setNaming] = useState(false);
  const [travelling, setTravelling] = useState(false);
  const [doubling, setDoubling] = useState(false);
  const p = opened(project);
  const entities = p.record.entities;
  const selected = entities.find((e) => e.id === params.who) ?? null;
  const [studies, showStudies] = useShown(characterStudiesOn(p));
  const [placeStudies, showPlaceStudies] = useShown(studyPagesOn(p, 'place'));
  const appearances = storyModel(project).appearances;
  const choose = (id: string | null) => navigate(hrefFor('plan/cast', null, id ? { who: id } : {}), { replace: true });
  return (
    <div className="story-page cast-page">
      <header className="page-head">
        <h1 tabIndex={-1}>Cast &amp; Places</h1>
        <p className="page-sub">{plural(entities.length, 'entry', 'entries')}</p>
      </header>
      <div className={`cast-layout${selected ? ' editing' : ''}`}>
        <nav className="cast-list" aria-label="Cast and places">
          {KINDS.map(({ kind, plural: heading }) => {
            const items = entities.filter((e) => e.kind === kind).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
            return (
              <section key={kind} className="cast-group" aria-label={heading} data-kind={kind}>
                <h2 className="eyebrow">{heading}</h2>
                <ul>
                  {items.map((e) => {
                    const href = hrefFor('plan/cast', null, { who: e.id });
                    return (
                      <li key={e.id}>
                        <a className={`cast-row${selected?.id === e.id ? ' selected' : ''}`} href={href} aria-current={selected?.id === e.id ? 'true' : undefined} data-entity-id={e.id} onClick={(ev) => followLink(ev, href)}>
                          <EntityDot color={e.color} />
                          <span>{e.name || 'Unnamed'}</span>
                          {kind === 'character' ? <span className="column-count">{appearances.get(e.id)?.scenes ?? 0}</span> : null}
                        </a>
                      </li>
                    );
                  })}
                </ul>
                {canWrite ? (
                  <button type="button" className="link-button" onClick={() => setAdding(kind)}>
                    + Add {ENTITY_KIND_LABELS[kind]}…
                  </button>
                ) : null}
                {kind === 'character' ? (
                  <>
                    <button type="button" className="link-button" onClick={() => setNaming(true)}>
                      Name Generator…
                    </button>
                    <label className="cast-toggle" title="Keep a page for each character in Research ▸ Characters">
                      <input type="checkbox" className="field-switch" checked={studies} disabled={!canWrite} onChange={(e) => {
                          const on = e.target.checked;
                          showStudies(on);
                          if (live) storyEdit(live, (x) => x.setCharacterStudies(on));
                        }} />
                      Study page for each character
                    </label>
                    {/* Radio companies double heavily: the panel is a hint for casting there too. */}
                    {project.kind === 'stage-play' || project.kind === 'radio-play' ? (
                      <button type="button" className="link-button" title="Which characters one actor could play" onClick={() => setDoubling(true)} data-doubling>
                        Doubling…
                      </button>
                    ) : null}
                  </>
                ) : null}
                {kind === 'place' ? (
                  <>
                    <button type="button" className="link-button" title="How people get about: today’s transport, or the ways of travelling of the story’s period" onClick={() => setTravelling(true)}>
                      Travel in the Story…
                    </button>
                    <label className="cast-toggle" title="Keep a page for each place in Research ▸ Places">
                      <input type="checkbox" className="field-switch" checked={placeStudies} disabled={!canWrite} onChange={(e) => {
                          const on = e.target.checked;
                          showPlaceStudies(on);
                          if (live) storyEdit(live, (x) => x.setStudyPages('place', on));
                        }} />
                      Study page for each place
                    </label>
                  </>
                ) : null}
              </section>
            );
          })}
        </nav>
        <div className="cast-detail">
          {selected ? (
            <EntityEditor key={selected.id} project={project} live={live} entity={selected} canWrite={canWrite} onBack={() => choose(null)} onDeleted={() => choose(null)} />
          ) : (
            <div className="story-empty" role="note">
              <p className="story-empty-title">Characters and places</p>
              <p>Add the characters, places, objects and motifs of your story. Scenes that name them (or an alias) are linked automatically; type @ in the editor to name one.</p>
            </div>
          )}
        </div>
      </div>
      {adding ? (
        <TextDialog
          title={`New ${ENTITY_KIND_LABELS[adding]}`}
          label="Name"
          confirm="Add"
          onCancel={() => setAdding(null)}
          onDone={(name) => {
            const kind = adding;
            setAdding(null);
            if (!live) return;
            const made = storyEdit(live, (e) => {
              const entity = e.createEntity(name, kind);
              e.ensureStudyPages();
              return entity;
            });
            announce(`${made.name} added`);
            choose(made.id);
          }}
        />
      ) : null}
      {travelling ? <TravelSheet project={project} live={live} canWrite={canWrite} onClose={() => setTravelling(false)} /> : null}
      {doubling ? <DoublingDialog project={project} onClose={() => setDoubling(false)} /> : null}
      {naming ? (
        <NameGenerator
          project={project}
          live={live}
          onClose={() => setNaming(false)}
          onShow={(id, study) => {
            setNaming(false);
            navigate(hrefFor(study ? 'plan/study' : 'plan/cast', null, { who: id }));
          }}
        />
      ) : null}
    </div>
  );
}

const hex = (rgb: string | undefined) => {
  const p = parseRGB(rgb);
  if (!p) return '#9a6b4f';
  return '#' + p.map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('');
};
const fromHex = (h: string) => {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(h);
  return m ? [m[1], m[2], m[3]].map((x) => (parseInt(x, 16) / 255).toFixed(3)).join(' ') : undefined;
};

let zones: string[] | null = null;
function timeZones(): string[] {
  if (zones) return zones;
  try {
    zones = (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    zones = [];
  }
  return zones;
}

function EntityEditor({ project, live, entity, canWrite, onBack, onDeleted }: {
  project: Project;
  live: LiveProject | null;
  entity: StoryEntity;
  canWrite: boolean;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const ws = useWorkspace();
  const [deleting, setDeleting] = useState(false);
  const [kind, showKind] = useShown<Kind>(entity.kind);
  const [color, showColor] = useShown<string | undefined>(entity.color);
  const [born, showBorn] = useShown<number | null>(entity.birth?.getTime() ?? null);
  const [zoneError, setZoneError] = useState<string | null>(null);
  /** Saves a change to the entity as it's stored now (another device's edits to other fields are kept). */
  const save = (change: Partial<StoryEntity>) => {
    if (!live || !canWrite) return;
    const current = opened(project).record.entities.find((e) => e.id === entity.id);
    if (!current) return;
    storyEdit(live, (e) => e.saveEntity({ ...current, ...change }));
  };
  const id = (s: string) => `entity-${s}`;
  const label = ENTITY_KIND_LABELS[entity.kind];
  const number = (text: string): number | undefined => {
    const n = Number(text.trim().replace(',', '.'));
    return text.trim() && Number.isFinite(n) ? n : undefined;
  };
  return (
    <form className="entity-editor" aria-label={entity.name || label} data-entity-id={entity.id} onSubmit={(e) => e.preventDefault()}>
      <button type="button" className="link-button cast-back" onClick={onBack}>
        ← All
      </button>
      <div className="entity-title">
        <EntityDot color={entity.color} size="large" />
        <h2>{entity.name || `Unnamed ${label.toLowerCase()}`}</h2>
        {entity.name ? <button type="button" className="link-button entity-lookup" onClick={() => lookUp(project, entity.name)} data-look-up>Dossier</button> : null}
      </div>
      <fieldset className="entity-section" disabled={!canWrite}>
        <div className="field-row">
          <label htmlFor={id('name')}>Name</label>
          <CommitInput id={id('name')} value={entity.name} onCommit={(t) => t.trim() && save({ name: t.trim() })} />
        </div>
        <div className="field-row">
          <label htmlFor={id('kind')}>Kind</label>
          <select id={id('kind')} value={kind} onChange={(e) => { showKind(e.target.value as Kind); save({ kind: e.target.value as Kind }); }}>
            {KINDS.map((k) => (
              <option key={k.kind} value={k.kind}>
                {ENTITY_KIND_LABELS[k.kind]}
              </option>
            ))}
          </select>
        </div>
        <div className="field-row">
          <label htmlFor={id('colour')}>Colour</label>
          <input id={id('colour')} type="color" value={hex(color)} onChange={(e) => { showColor(fromHex(e.target.value)); save({ color: fromHex(e.target.value) }); }} />
        </div>
        <div className="field-row stacked">
          <label htmlFor={id('aliases')}>Also called</label>
          <CommitInput
            id={id('aliases')}
            value={entity.aliases.join(', ')}
            onCommit={(t) => save({ aliases: t.split(',').map((s) => s.trim()).filter(Boolean) })}
          />
          <p className="field-note">Scenes that use the name or any of these (separated by commas) are linked to this {label.toLowerCase()} automatically.</p>
        </div>
      </fieldset>
      {entity.kind === 'place' && entity.studyID ? (
        <p>
          <button type="button" className="link-button" onClick={() => ws?.reveal({ fragmentId: entity.studyID! })}>
            Open Study Page
          </button>
        </p>
      ) : null}
      {entity.kind === 'character' ? (
        <fieldset className="entity-section" disabled={!canWrite}>
          <legend className="eyebrow">Character</legend>
          <div className="field-row stacked">
            <label htmlFor={id('role')}>Role</label>
            <CommitInput id={id('role')} value={entity.role ?? ''} onCommit={(t) => save({ role: t.trim() || undefined })} />
            <p className="field-note">Their part in the story, in a line.</p>
          </div>
          {entity.studyID ? (
            <p>
              <button type="button" className="link-button" onClick={() => ws?.reveal({ fragmentId: entity.studyID! })}>
                Open Study Page
              </button>
            </p>
          ) : null}
          <div className="field-row">
            <label htmlFor={id('born-known')}>Birth date known</label>
            <input
              id={id('born-known')}
              type="checkbox"
              className="field-switch"
              checked={born !== null}
              onChange={(e) => {
                const on = e.target.checked ? (entity.birth?.getTime() ?? Date.now()) : null;
                showBorn(on);
                save({ birth: on === null ? undefined : new Date(on) });
              }}
            />
          </div>
          {born !== null ? (
            <div className="field-row">
              <label htmlFor={id('born')}>Born</label>
              <input
                id={id('born')}
                type="date"
                value={toDateInput(born)}
                onChange={(e) => {
                  const ms = fromDateInput(e.target.value);
                  if (ms === null) return;
                  showBorn(ms);
                  save({ birth: new Date(ms) });
                }}
              />
            </div>
          ) : null}
        </fieldset>
      ) : null}
      {entity.kind === 'place' ? (
        <fieldset className="entity-section" disabled={!canWrite}>
          <legend className="eyebrow">On the map</legend>
          <div className="field-row">
            <label htmlFor={id('lat')}>Latitude</label>
            <CommitInput id={id('lat')} numeric value={entity.latitude === undefined ? '' : String(entity.latitude)} onCommit={(t) => save({ latitude: number(t) })} />
          </div>
          <div className="field-row">
            <label htmlFor={id('lon')}>Longitude</label>
            <CommitInput id={id('lon')} numeric value={entity.longitude === undefined ? '' : String(entity.longitude)} onCommit={(t) => save({ longitude: number(t) })} />
          </div>
          <div className="field-row stacked">
            <label htmlFor={id('zone')}>Clock</label>
            <CommitInput
              id={id('zone')}
              value={entity.timeZoneID ?? ''}
              onCommit={(t) => {
                const z = t.trim();
                if (z && !timeZones().includes(z)) {
                  try {
                    new Intl.DateTimeFormat('en', { timeZone: z });
                  } catch {
                    setZoneError(`“${z}” isn’t a time zone this browser knows (try one like Europe/London).`);
                    return;
                  }
                }
                setZoneError(null);
                save({ timeZoneID: z || undefined });
              }}
            />
            <p className="field-note">{entity.timeZoneID ? `Time zone ${entity.timeZoneID}.` : 'Local mean time, from the longitude.'} A time zone such as Europe/London; leave it empty for local mean time.</p>
            {zoneError ? <p className="error" role="alert">{zoneError}</p> : null}
          </div>
          {entity.timeZoneID ? (
            <p>
              <button type="button" className="link-button" onClick={() => save({ timeZoneID: undefined })}>
                Use local mean time (before time zones)
              </button>
            </p>
          ) : null}
          <p className="field-note">The position gives sunrise, sunset and the moon’s phase for scenes set here. Searching the map for a place is on your Mac or iPad; here, type its latitude and longitude.</p>
        </fieldset>
      ) : null}
      {entity.kind === 'place' && entity.latitude != null && entity.longitude != null && mappedPlacesBesides(project, entity).length ? (
        <PlaceDistances place={entity} others={mappedPlacesBesides(project, entity)} travel={travelOf(project)} />
      ) : null}
      <div className="entity-section">
        <label className="eyebrow" htmlFor={id('notes')}>
          Notes
        </label>
        <CommitTextArea id={id('notes')} className="entity-notes" rows={5} value={entity.notes} readOnly={!canWrite} onCommit={(t) => save({ notes: t })} />
      </div>
      {canWrite ? (
        <p>
          <button type="button" className="link-button danger" onClick={() => setDeleting(true)}>
            Delete {label}…
          </button>
        </p>
      ) : null}
      {deleting ? (
        <ConfirmDialog
          title={`Delete ${entity.name}?`}
          message={<p>Scenes keep their text; they just stop being linked to {entity.name}.</p>}
          confirm="Delete"
          onCancel={() => setDeleting(false)}
          onConfirm={() => {
            setDeleting(false);
            if (live) storyEdit(live, (e) => e.deleteEntity(entity.id));
            announce(`${entity.name} deleted`);
            onDeleted();
          }}
        />
      ) : null}
    </form>
  );
}

/** A play's doubling (`DoublingSheet` on the Mac): the fewest actors who could play its characters, one actor taking roles
 *  that are never on stage in the same scene, and where an actor would go straight from one role into the next scene. */
export function DoublingDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const p = opened(project);
  const model = storyModel(project);
  const d = suggestDoubling(model.co.order, model.rows.map((r) => ({ scene: r.fragmentID, entity: r.entityID, level: r.level })), model.people.map((e) => e.id));
  const entity = (id: string) => p.record.entities.find((e) => e.id === id);
  const name = (id: string) => entity(id)?.name ?? '?';
  const scene = (id: string) => p.fragmentRecord(id)?.title || 'Untitled';
  const summary = d.actors.filter((a) => a.roles.length > 1).map((a) => {
    const names = a.roles.map(name);
    return `The same actor plays ${names.length === 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`}.`;
  }).join('\n\n');
  return (
    <DialogShell title="Doubling" onCancel={onClose} className="doubling">
      {d.characters === 0 ? (
        <p>Nobody is on stage yet. A character is on stage in a scene where they speak, or where you’ve set them on stage in the scene’s details.</p>
      ) : (
        <p data-doubling-summary>
          {plural(d.actors.length, 'actor', 'actors')} could play the {plural(d.characters, 'character', 'characters')} who appear, one actor taking roles that are never on stage in the same scene.
        </p>
      )}
      {d.actors.length ? (
        <ol className="doubling-actors">
          {d.actors.map((a, n) => (
            <li key={n} data-actor={n + 1}>
              <span className="eyebrow">Actor {n + 1} · {plural(a.scenes, 'scene', 'scenes')}</span>
              <span className="doubling-roles">
                {a.roles.map((id) => (
                  <span key={id} className="doubling-role"><EntityDot color={entity(id)?.color} /> {name(id)}</span>
                ))}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      {d.quickChanges.length ? (
        <>
          <h3 className="eyebrow">Quick changes</h3>
          <ul className="doubling-changes">
            {d.quickChanges.map((c, i) => <li key={i}>{name(c.from)} to {name(c.to)}, straight into “{scene(c.scene)}”</li>)}
          </ul>
          <p className="page-sub">No scene between to change costume: the director may cast these apart.</p>
        </>
      ) : null}
      <div className="actions">
        <button type="button" className="button secondary" disabled={summary === ''} title="Copy the doubling, to paste under the Cast of Characters in Publishing ▸ Front Matter"
          onClick={() => { void navigator.clipboard?.writeText(summary); announce('Copied the doubling'); }}>
          Copy
        </button>
        <button type="button" className="button primary" onClick={onClose}>Done</button>
      </div>
    </DialogShell>
  );
}
